/* globals window */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { css } from '@emotion/react';
import { buttonOutlineBlack, breakpoints } from '@birdi/theme';
import { blocks } from '@birdi/theme';
import { UserTable } from './UserTable';
import { InviteUserModal } from './invite-user-modal';
import { SeatsBadge } from '../plan-and-usage/seats/seats-badge';
import { useMetaSelector } from '../../meta/reducer';
import { Pagination } from '@birdi/pagination';
import {
  stroot,
  organisationUsers,
  organisation as organisationAPI,
} from '@birdi/js-sdk/src/public';
import RoleSelect from './role-select';
import SearchSVG from '@birdi/icons/search.svg';
import { FeatureFlagNames } from '@birdi/js-sdk/src/public/organisation-features';

const LOAD_AMOUNT = 20;

interface Filters {
  search: string;
  roleId: string;
}

export const Users: React.FC = () => {
  // TODO: Self-contained modal
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [showManageSeatsModal, setShowManageSeatsModal] = useState(false);
  const meta = useMetaSelector();
  const [orgName, setOrgName] = useState('');
  // const users = useSelector((state) => state.users);

  const [usersList, setUsersList] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [filters, setFilters] = useState<Filters>({
    search: '',
    roleId: null,
  });
  const [start, setStart] = useState(1);
  const [count, setCount] = useState(0);

  const [isPrimary, setIsPrimary] = useState(null);
  const [showWorkspaceColumn, setShowWorkspaceColumn] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  const { pathname } = location;
  let workspaceId = null;
  if (pathname.includes('workspace-users')) {
    workspaceId = pathname.substring(pathname.lastIndexOf('/') + 1);
    organisationAPI.getOneById(stroot('iy3ohb'), workspaceId).then((res) => {
      setOrgName(res.body.name);
    });
  }

  const isWorkspaceAdmin = meta.permissions.includes('workspace:user:create');

  useEffect(() => {
    if (!meta.loaded) return;

    const isPrimaryOrg = !meta.workspace;
    setIsPrimary(isPrimaryOrg);

    const workspaceEnabled =
      meta.features.includes('add-workspaces') &&
      isPrimaryOrg &&
      isWorkspaceAdmin;

    const showColumn =
      workspaceEnabled && location.pathname.includes('admin-users');
    setShowWorkspaceColumn(showColumn);
  }, [meta.loaded, location.pathname]);

  const setResponseValues = (response) => {
    setUsersLoading(false);
    setUsersList(response.items);
    setCount(response.itemCount);
  };

  const searchUsers = (filters: Filters, start: number = 1) => {
    setUsersLoading(true);
    const params = {
      start,
      limit: LOAD_AMOUNT,
      allSubWorkspaces: showWorkspaceColumn ? 'yes' : 'no',
      ...(filters.search && { search: filters.search }),
      ...(filters.roleId && { roleId: filters.roleId }),
      ...(workspaceId && { workspaceId }),
    };
    organisationUsers.getUsers(stroot('iteih4'), params).then((res) => {
      setResponseValues(res.body);
    });
  };

  useEffect(() => {
    if (meta.loaded && showWorkspaceColumn !== null && isPrimary !== null) {
      searchUsers(filters);
    }
  }, [isPrimary]);

  useEffect(() => {
    if (meta.loaded && showWorkspaceColumn !== null && isPrimary !== null) {
      searchUsers(filters);
    }

    function openModalOnSearch() {
      const modalParam = new URLSearchParams(window.location.search).get(
        'modal',
      );
      if (modalParam === 'invite') {
        setShowManageSeatsModal(false);
        setShowInvitationModal(true);
        // Removes query
        window.history.replaceState('users', 'Birdi', '/admin-users');
      }
      if (modalParam === 'seats') {
        setShowInvitationModal(false);
        setShowManageSeatsModal(true);
        window.history.replaceState('users', 'Birdi', '/admin-users');
      }
      if (modalParam === 'inviteToWorkspace' && workspaceId) {
        setShowManageSeatsModal(false);
        setShowInvitationModal(true);
        window.history.replaceState(
          'users',
          'Birdi',
          `/workspace-users/${workspaceId}`,
        );
      }
    }
    openModalOnSearch();
  }, [location]);

  const updateFilters = (updatedFilter: Partial<Filters>) => {
    setFilters({ ...filters, ...updatedFilter });
    setStart(1);
    searchUsers({ ...filters, ...updatedFilter });
  };

  const updatePage = (page: number) => {
    setStart(page);
    searchUsers(filters, page);
  };

  return (
    <div>
      <div css={blocks.pageTitleHolder}>
        <h2 css={blocks.pageTitle}>
          Users &nbsp;&nbsp;
          <span
            css={(theme) => css`
              color: ${theme.mono40};
            `}
          >
            {orgName}
          </span>
        </h2>
      </div>
      <div
        css={(theme) => css`
          ${blocks.pageGraySection(theme)};
          justify-content: space-between;
          @media (max-width: ${breakpoints.m}) {
            padding-bottom: 1em;
          }
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: row;
            align-items: center;
            @media (max-width: ${breakpoints.m}) {
              flex-direction: column;
              justify-content: flex-start;
              align-items: flex-start;
              p {
                margin-bottom: 0;
              }
            }
          `}
        >
          <p
            css={css`
              margin-right: 0.5em;
            `}
          >
            {!workspaceId
              ? 'Manage, update and invite team members to join your organization.'
              : 'Manage users in your workspace.'}
          </p>
          {isPrimary && !workspaceId && (
            <div
              css={css`
                margin-top: 1em;
              `}
            >
              <SeatsBadge
                modalOpen={showManageSeatsModal}
                setModalOpen={setShowManageSeatsModal}
              />
            </div>
          )}
        </div>
        {isWorkspaceAdmin && (
          <Link
            // to={inviteDisabled ? '#' : '/admin-users?modal=invite'}
            to={
              !workspaceId
                ? '/admin-users?modal=invite'
                : `/workspace-users/${workspaceId}?modal=inviteToWorkspace`
            }
            css={(theme) => css`
              ${buttonOutlineBlack(theme)};
              background-color: ${theme.mono0};
              border: 1px solid ${theme.mono40};
              border-radius: 4px;
              height: 2em;
              span {
                color: ${theme.mono50};
              }
            `}
          >
            <span id="invite-a-user">+ Invite a user</span>
          </Link>
        )}
      </div>
      {isWorkspaceAdmin && (
        <div>
          <InviteUserModal
            open={showInvitationModal}
            setOpen={setShowInvitationModal}
            workspaceId={workspaceId}
          />
        </div>
      )}

      <div
        css={(theme) => css`
          ${blocks.separator2(theme)}
        `}
      >
        <form
          role="search"
          onSubmit={(event) => {
            event.preventDefault();
          }}
          css={css`
            flex-grow: 1;
            display: flex;
            flex-wrap: wrap;
            & > *:not(last-child) {
              margin-right: 0.5em;
            }
            gap: 0.5em;
            @media (max-width: ${breakpoints.xs}) {
              flex-direction: column;
            }
          `}
        >
          <div
            css={css`
              position: relative;
              display: inline-block;
            `}
          >
            <SearchSVG
              css={(theme) => css`
                position: absolute;
                top: 50%;
                left: 10px;
                transform: translateY(-50%);
                pointer-events: none;
                height: 1em;
                width: 1em;
                color: ${theme.mono40};
              `}
            />
            <input
              id="name"
              type="text"
              value={filters.search}
              placeholder="Search by name"
              onChange={(event) =>
                updateFilters({ search: event.target.value })
              }
              css={(theme) => css`
                ${blocks.input(theme)};
                border: 1px solid #ccc !important;
                min-width: 20em;
                padding-left: 2em;
                &::placeholder {
                  font-size: 1em;
                }
              `}
            />
          </div>
          <RoleSelect
            onChange={(role) =>
              updateFilters({ roleId: role ? role.value : null })
            }
          />
        </form>
      </div>

      {meta.loaded && usersList.length > 0 && showWorkspaceColumn !== null ? (
        <section
          css={css`
            padding-bottom: 2em;
          `}
        >
          <UserTable
            users={usersList}
            id={meta.id}
            permissions={meta.permissions}
            workspaceId={workspaceId}
            isMain={showWorkspaceColumn}
          />
          <div
            css={css`
              padding: 1em 0;
            `}
          >
            <Pagination
              onChange={(currentPage) => updatePage(currentPage)}
              itemCount={count}
              itemsPerPage={LOAD_AMOUNT}
              currentPage={start}
            />
          </div>
        </section>
      ) : (
        <div
          css={css`
            padding: 2em;
          `}
        >
          {usersLoading ? 'Loading users ...' : 'No users found!'}
        </div>
      )}
    </div>
  );
};
